import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
	Box,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Toolbar,
	AppBar,
	IconButton,
	Typography,
	Divider,
	Avatar,
	Tooltip
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SpaIcon from '@mui/icons-material/Spa';
import StoreIcon from '@mui/icons-material/Store';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Farmer360logo from '../../assets/Farmer360logo.png';

const drawerWidth = 220;

const navItems = [
	{ label: 'Dashboard', icon: <DashboardIcon />, path: '/masterData/dashboard' },
	{ label: 'User', icon: <PeopleIcon /> ,path:'/masterData/user' ,related: ['/masterData/user', '/masterData/usercreate']},
	{ label: 'Location', icon: <LocationOnIcon />, path: '/masterData/location' },
	{ label: 'Farmer', icon: <AgricultureIcon />,path:'/masterData/farmer', related: ['/masterData/farmer', '/masterData/createFarmer'] },
	{ label: 'Pre Activity', icon: <ListAltIcon /> ,path:'/masterData/preActivity', related: ['/masterData/preActivity', '/masterData/addTarget']},
	{ label: 'Post Activity', icon: <AssignmentTurnedInIcon />,path:'/masterData/postActivity', related: ['/masterData/postActivity', '/masterData/createPostActivity'] },
	{ label: 'Sales Target', icon: <TrendingUpIcon />, path: '/masterData/salesTarget', related: ['/masterData/salesTarget', '/masterData/createSalesTarget'] },
	{ label:'Daily Activity', icon:<ListAltIcon />, path:'/masterData/travelPunchingLogList'},
	{ label: 'Daily Task', icon: <AssignmentTurnedInIcon /> ,path:'/masterData/dailyTask'},
	{ label: 'Season', icon: <CalendarMonthIcon />, path: '/masterData/seasons', related: ['/masterData/seasons', '/masterdata/seasonscreate'] },
	{ label: 'Crop & Variety', icon: <SpaIcon />,path:'/masterData/cropVariety' },
	{ label: 'Dealer', icon: <StoreIcon /> ,path:'/masterData/dealer' ,related: ['/masterData/dealer', '/masterData/createDealer']},

];

function Sidebar() {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [mobileOpen, setMobileOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [userName, setUserName] = useState('');

	useEffect(() => {

		const idx = navItems.findIndex((item) => {
			// Check main path, startsWith, or related paths
			if (location.pathname === item.path || location.pathname.startsWith(item.path)) return true;
			if (item.related && Array.isArray(item.related)) {
				return item.related.some(rel => location.pathname === rel || location.pathname.startsWith(rel));
			}
			return false;
		});
		setSelectedIndex(idx === -1 ? null : idx);
	}, [location.pathname]);

	  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('userDetails'));
    if (storedUser?.name) {
      setUserName(storedUser.name);
    }
  }, []);

	const handleDrawerToggle = () => {
		setMobileOpen((prev) => !prev);
	};

	const handleLogoClick = () => {
		const userDetails = JSON.parse(localStorage.getItem('userDetails'));
		if (!userDetails?.token) {
			// User not logged in, navigate to home
			navigate('/');
		} else {
			// User logged in, navigate to appropriate dashboard based on role
			if (userDetails.role === 'ADMIN' || userDetails.role === 'ZM' || userDetails.role === 'AE') {
				navigate('/masterData/dashboard');
			} else if (userDetails.role === 'RM' || userDetails.role === 'AM') {
				navigate('/amrm');
			} else if (userDetails.role === 'SO' || userDetails.role === 'MDO') {
				navigate('/SOorMDO/farmerCommitment');
			} else {
				navigate('/');
			}
		}
	};

	const handleItemClick = (index) => {
		setSelectedIndex(index);
		const item = navItems[index];
		if (item.path) {
			navigate(item.path);
		}
		if (isMobile) setMobileOpen(false);
	};

	const drawerContent = (
		<Box role="presentation" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<Box 
				sx={{ 
					display: 'flex', 
					justifyContent: 'center', 
					alignItems: 'center', 
					mt: 1,
					cursor: 'pointer',
					'&:hover': {
						opacity: 0.8
					}
				}}
				onClick={handleLogoClick}
			>
				<img src={Farmer360logo} alt="Farmer360" style={{ width: 150, height: 80, objectFit: 'contain' }} />
			</Box>
			<Divider />
			<List sx={{ px: 1 }}>
				{navItems.map((item, index) => (
					<ListItem key={item.label} disablePadding>
						<ListItemButton
							onClick={() => handleItemClick(index)}
							selected={selectedIndex === index}
							sx={{
								borderRadius: 1,
								position: 'relative',
								'&.Mui-selected': {
									backgroundColor: 'transparent',
								},
								'&.Mui-selected .MuiListItemText-primary': {
									color: '#4318FF',
								},
								'&.Mui-selected .MuiSvgIcon-root': {
									color: '#4318FF',
								},
								'&.Mui-selected::after': {
									content: '""',
									position: 'absolute',
									right: 0,
									top: '50%',
									transform: 'translateY(-50%)',
									width: '3px',
									height: '60%',
									borderRadius: '3px',
									backgroundColor: '#4318FF',
								},
							}}
						>
							<ListItemIcon sx={{ minWidth: 20, '& .MuiSvgIcon-root': { width: 18.4, height: 16.6 } }}>{item.icon}</ListItemIcon>
							<ListItemText
								primary={item.label}
								slotProps={{
									primary: {
										sx: { fontFamily: 'DM Sans, sans-serif', fontSize: '16px', ml: 1.5 },
									},
								}}
							/>
						</ListItemButton>
					</ListItem>
				))}
			</List>
			<Box sx={{ flexGrow: 1 }} />
			{/* Profile icon at the bottom */}
			<Box sx={{ p: 2,  display: 'flex', alignItems: 'center',  gap: 1 }}>
				<Tooltip title="Profile">
					<IconButton onClick={() => navigate('/profile')} size="large">
						<Avatar sx={{ bgcolor: '#4318FF', width: 40, height: 40 }}>
							<AccountCircleIcon sx={{ width: 32, height: 32 }} />
						</Avatar>
					</IconButton>
				</Tooltip>
				 {userName && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'black',
              maxWidth: 140,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </Typography>
        )}
			</Box>
		</Box>
	);

	return (
		<Box sx={{ display: 'flex' }}>
			{/* Mobile top bar with menu icon */}
			{isMobile && (
				<AppBar
					position="fixed"
					color="inherit"
					elevation={0}
					sx={{ display: { xs: 'block', sm: 'block', md: 'none' }, borderBottom: 0.5, borderColor: 'transparent' }}
				>
					<Toolbar variant="dense" sx={{ minHeight: 56 }}>
						<IconButton edge="start" aria-label="open menu" onClick={handleDrawerToggle}>
							<MenuIcon />
						</IconButton>
						<Typography variant="subtitle1" sx={{ ml: 1 }}>
							Menu
						</Typography>
					</Toolbar>
				</AppBar>
			)}

			<Box component="nav" aria-label="sidebar navigation" sx={{ flexShrink: { md: 0 } }}>
				{/* Temporary drawer on mobile */}
				<Drawer
					variant={isMobile ? 'temporary' : 'permanent'}
					open={isMobile ? mobileOpen : true}
					onClose={handleDrawerToggle}
					ModalProps={{ keepMounted: true }}
					sx={{
						'& .MuiDrawer-paper': {
							width: drawerWidth,
							backgroundColor: '#FFFFFF',
							boxSizing: 'border-box',
						},
					}}
				>
					{drawerContent}
				</Drawer>
			</Box>
		</Box>
	);
}

export default Sidebar;

